import '../../App.css';
import axios from 'axios';
import { useEffect, useState, useRef } from 'react';
import Highlightable from 'highlightable';
import React from 'react';
import SelectionToolbar from '../SelectionToolbar';
import { logHighlightEvent } from '../../logging';
import { LowPassFilter, OneEuroFilter } from '../OneEuroFilter';
import { ProgressCircle, Tooltip } from '@adobe/react-spectrum';

function TweakingTrailingWidgetDocument({ mouseWidgetWidth, ...properties }) {
    // const [mouseWidgetInfo, setMouseWidgetInfo] = useState({ x: 0, y: 0, show: false, value: 0 });
    const [mouseWidgetPosition, setMouseWidgetPosition] = useState({ x: 0, y: 0 });
    const userNeedsToSlow = useRef(false);
    const userBubblePopped = useRef(false);
    const mouseIsDown = useRef(false);
    const mouseWidgetValueTimerRef = useRef(null);
    const [mouseWidgetInstructions, setMouseWidgetInstructions] = useState("Keep your cursor in the circle.");
    const [tooltipOffset, setTooltipOffset] = useState({ x: mouseWidgetWidth / 2, y: mouseWidgetWidth / 2 });
    const tooltipRef = useRef(null);
    const mouseWidgetRef = useRef(null);
    const poppedTooltipRef = useRef(null);
    const [poppedTooltipOffset, setPoppedTooltipOffset] = useState({ x: 0, y: 0 });
    const [showPoppedTooltip, setShowPoppedTooltip] = useState(false);
    const mouseWidgetBounds = useRef({ width: mouseWidgetWidth, height: mouseWidgetWidth });
    const mouseDownPosition = useRef({ x: 0, y: 0, timestamp: 0 });
    const [mouseWidgetWidthMultiplier, setMouseWidgetWidthMultiplier] = useState(1);
    const mouseWidgetWidthMultiplierRef = useRef(1); // animate function needs this to be a ref
    const [showMouseWidget, setShowMouseWidget] = useState(false);
    const poppingAnimationStartTime = useRef(0);
    const poppingAnimationEndTime = useRef(0);

    // One Euro Filter
    let frequency = 120; // Hz
    // const MAX_FC_MIN = 10.0;
    // const MIN_FC_MIN = 0.04;
    let fcMin = 1.0;
    let beta = 0;
    let dcutoff = 1.0;
    const filters = useRef({ fx: new OneEuroFilter(frequency, fcMin, beta, dcutoff), fy: new OneEuroFilter(frequency, fcMin, beta, dcutoff) });
    // const filters = useRef({ fx: new LowPassFilter(0.5), fy: new LowPassFilter(0.5) });

    const mouseMovements = useRef([]);
    const [latestMousePosition, setLatestMousePosition] = useState({ x: 0, y: 0 });
    const latestFilteredMousePosition = useRef({ x: 0, y: 0 });
    const latestMousePositionRef = useRef({ x: 0, y: 0 }); // animate function needs this to be a ref
    const requestRef = useRef();
    const poppingRequestRef = useRef();
    const poppingMouseWidgetPosition = useRef({ x: 0, y: 0 });

    function checkOverlappingRanges(left, right) {
        // the two ranges overlap
        if (right.start <= left.end) {
            return true
        }
        return false;
    }

    function mergeOverlappingRanges(left, right) {
        var newStart = Math.min(left.start, right.start);
        var newEnd = Math.max(left.end, right.end);
        var highlight = true;
        var showToolbar = false;

        // check whether the content should be highlighted right away
        if (properties.selectedTool !== "highlighter") {
            highlight = false;
            showToolbar = true;
        }
        return { start: newStart, end: newEnd, text: properties.passage.substring(newStart, newEnd + 1), highlight: highlight, showToolbar: showToolbar };
    }

    function mergeAllHighlights(newHighlight) {
        var highlights = properties.highlights.concat(newHighlight).sort((a, b) => a.start - b.start);

        function addDataToHighlight(highlightToEdit) {
            var highlight = true;
            var showToolbar = false;

            // check whether the content should be highlighted right away
            if (properties.selectedTool !== "highlighter") {
                highlight = false;
                showToolbar = true;
            }
            highlightToEdit.highlight = highlight;
            highlightToEdit.showToolbar = showToolbar;
        }

        for (var i = 0; i < highlights.length - 1; i++) {
            var left = highlights[i];
            var right = highlights[i + 1];
            if (checkOverlappingRanges(left, right)) {
                var merged = mergeOverlappingRanges(left, right);
                highlights.splice(i, 2, merged);
                i--;
            }
        }

        for (var i = 0; i < highlights.length; i++) {
            if (highlights[i].highlight === undefined || highlights[i].showToolbar === undefined) {
                addDataToHighlight(highlights[i]);
            }
        }


        return highlights;
    }

    function getHighlightedText(highlights) {
        var text = "";
        for (var i = 0; i < highlights.length; i++) {
            text += highlights[i].text + " ";
        }
        return text;
    }

    function checkRangeValid(range) {
        var rangeContents = document.getSelection().getRangeAt(0).cloneContents();
        var allSpans = rangeContents.querySelectorAll('span');
        var selectionSpans = [];
        allSpans.forEach((span) => {
            if (span.getAttribute('data-position') !== null) {
                selectionSpans.push(span);
            }
        });

        var startSpan = selectionSpans[0];
        var endSpan = selectionSpans[selectionSpans.length - 1];
        if (startSpan === undefined || endSpan === undefined) {
            return range;
        }

        // first check for any NaN values
        if (isNaN(range.start) || isNaN(range.end)) {
            var startIndex = parseInt(startSpan.getAttribute('data-position'));
            var endIndex = parseInt(endSpan.getAttribute('data-position'));
            range.start = startIndex;
            range.end = endIndex;
            range.text = properties.passage.substring(startIndex, endIndex + 1);
        }
        return range;
    }


    function onTextHighlighted() {
        // they popped the bubble, so don't make a new highlight
        if (userNeedsToSlow.current && userBubblePopped.current) {
            return;
        }

        var sel = document.getSelection();
        var unsnappedStartIndex = parseInt(sel.anchorNode.parentNode.getAttribute('data-position'));
        var unsnappedEndIndex = parseInt(sel.focusNode.parentNode.getAttribute('data-position'));
        var unsnappedText = properties.passage.substring(unsnappedStartIndex, unsnappedEndIndex + 1);
        var unsnappedRange = { start: unsnappedStartIndex, end: unsnappedEndIndex, text: unsnappedText };

        let startNodeBounds = sel.anchorNode.parentNode.getBoundingClientRect().toJSON();
        let endNodeBounds = sel.focusNode.parentNode.getBoundingClientRect().toJSON();
        
        var wordSelection = snapSelectionToWord();
        var startNode = wordSelection.anchorNode.parentNode;
        var endNode = wordSelection.focusNode.parentNode;

        var startIndex = parseInt(startNode.getAttribute('data-position'));
        var endIndex = parseInt(endNode.getAttribute('data-position'));
        var leftToRight = startIndex < endIndex;
        // swap order if not left to right
        if (!leftToRight) {
            var tmpNode = endNode;
            endNode = startNode;
            startNode = tmpNode;

            var tmpIndex = endIndex;
            endIndex = startIndex;
            startIndex = tmpIndex;
        }

        var snappedRange = { start: startIndex, end: endIndex, text: properties.passage.substring(startIndex, endIndex + 1) };
        checkRangeValid(snappedRange);
        var mergedHighlights = mergeAllHighlights(snappedRange);
        var highlightedText = getHighlightedText(mergedHighlights);
        var newWordCount = properties.countNumWords(highlightedText);
        var validHighlight = true;

        if (newWordCount <= properties.maxWordCount || !properties.constrainHighlights) {
            // do not change the word count with the cursor tool, otherwise you double count
            if (properties.selectedTool === "highlighter") {
                properties.setWordCount(newWordCount);
            }
            properties.setHighlights(mergedHighlights);
            properties.setShowError(false);
            document.getSelection().removeAllRanges();
            validHighlight = true;
        }

        else if (newWordCount > properties.maxWordCount) {
            properties.setShowError(true);
            validHighlight = false;
        }

        let mousePosition = { mouseDown: mouseDownPosition.current, latestMousePosition: latestMousePositionRef.current };
        var log = {
            type: "add",
            timestamp: Date.now(),
            condition: properties.condition,
            experiment: properties.experiment,
            workerId: properties.workerId,
            participantId: properties.participantId,
            pass: properties.pass,

            tweakedParameter: mouseWidgetWidth,
            selectedLevel: properties.selectedLevel,
            startNodeBounds: startNodeBounds,
            endNodeBounds: endNodeBounds,
            mousePosition: mousePosition,

            unsnappedRange: unsnappedRange,
            snappedRange: snappedRange,
            newWordCount: newWordCount,
        }

        logHighlightEvent(log);
    }

    // helper function to snap the selection to words - https://stackoverflow.com/questions/7380190/select-whole-word-with-getselection
    function snapSelectionToWord() {
        var sel;

        // Check for existence of window.getSelection() and that it has a
        // modify() method. IE 9 has both selection APIs but no modify() method.
        if (window.getSelection && (sel = window.getSelection()).modify) {
            sel = window.getSelection();
            if (!sel.isCollapsed) {
                // Detect if selection is backwards
                var range = document.createRange();
                range.setStart(sel.anchorNode, sel.anchorOffset);
                range.setEnd(sel.focusNode, sel.focusOffset);
                var backwards = range.collapsed;
                range.detach();

                // modify() works on the focus of the selection
                var endNode = sel.focusNode,
                    endOffset = sel.focusOffset;
                sel.collapse(sel.anchorNode, sel.anchorOffset);

                var direction = [];
                if (backwards) {
                    direction = ['backward', 'forward'];
                } else {
                    direction = ['forward', 'backward'];
                }

                sel.modify("move", direction[0], "character");
                sel.modify("move", direction[1], "word");
                sel.extend(endNode, endOffset);
                sel.modify("extend", direction[1], "character");
                sel.modify("extend", direction[0], "word");

            }
        } else if ((sel === document.selection) && sel.type !== "Control") {
            var textRange = sel.createRange();
            if (textRange.text) {
                textRange.expand("word");
                // Move the end back to not include the word's trailing space(s),
                // if necessary
                while (/\s$/.test(textRange.text)) {
                    textRange.moveEnd("character", -1);
                }
                textRange.select();
            }
        }

        return sel;
    }

    function filterSelections(element) {
        return element.highlight;
    }

    // helper function to count the number of potential words after a selection, without double counting existing highlights
    function countNumPotentialWords() {
        var sel = document.getSelection();
        if (sel.toString() === "") {
            return properties.wordCount;
        }

        else {
            var unsnappedStartIndex = parseInt(sel.anchorNode.parentNode.getAttribute('data-position'));
            var unsnappedEndIndex = parseInt(sel.focusNode.parentNode.getAttribute('data-position'));
            var unsnappedText = properties.passage.substring(unsnappedStartIndex, unsnappedEndIndex + 1);
            var unsnappedRange = { start: unsnappedStartIndex, end: unsnappedEndIndex, text: unsnappedText };

            var wordSelection = snapSelectionToWord();
            var startNode = wordSelection.anchorNode.parentNode;
            var endNode = wordSelection.focusNode.parentNode;

            var startIndex = parseInt(startNode.getAttribute('data-position'));
            var endIndex = parseInt(endNode.getAttribute('data-position'));
            var leftToRight = startIndex < endIndex;
            // swap order if not left to right
            if (!leftToRight) {
                var tmpNode = endNode;
                endNode = startNode;
                startNode = tmpNode;

                var tmpIndex = endIndex;
                endIndex = startIndex;
                startIndex = tmpIndex;
            }

            var snappedRange = { start: startIndex, end: endIndex, text: properties.passage.substring(startIndex, endIndex + 1), blurAmount: 0 };
            checkRangeValid(snappedRange);
            var mergedHighlights = mergeAllHighlights(snappedRange);
            var highlightedText = getHighlightedText(mergedHighlights);
            var newWordCount = properties.countNumWords(highlightedText);
            return newWordCount;
        }




        // return properties.wordCount + properties.countNumWords(sel.toString());
    }

    // filter out the selections that are not highlighted
    function mouseDown(e) {
        if (e.target.className !== "blackButton") {
            // hide temporary highlights
            var filteredHighlights = properties.highlights.filter(filterSelections);

            // hide all black buttons
            for (var i = 0; i < filteredHighlights.length; i++) {
                filteredHighlights[i].showToolbar = false;
            }

            properties.setHighlights(filteredHighlights);
            properties.setShowError(false);
        }

        setShowPoppedTooltip(false);
        mouseWidgetRef.current.style.backgroundColor = "black";
        mouseDownPosition.current = { x: e.clientX, y: e.clientY, timestamp: Date.now() };

        // only show the mouse widget if the user is not clicking on an existing highlight
        if (e.target.getAttribute('range') === null && e.target.className !== "blackButton") {

            mouseIsDown.current = true;
            userBubblePopped.current = false;

            mouseWidgetWidthMultiplierRef.current = 1;
            setMouseWidgetWidthMultiplier(1);

            // TWEAKING: always show the mouse widget
            customizeTooltip();

            userNeedsToSlow.current = true;
            setShowMouseWidget(true);

            // clear the mouse movements
            mouseMovements.current = [{ x: e.clientX, y: e.clientY, added: "mouseDown" }];
            setMouseWidgetPosition({ x: e.clientX - (mouseWidgetBounds.current.width / 2), y: e.clientY - (mouseWidgetBounds.current.height / 2) });


        }
    }

    function mouseUp(e) {
        mouseIsDown.current = false;
        setShowMouseWidget(false);
        clearInterval(mouseWidgetValueTimerRef.current);
    }

    function mapRange(value, originalMin, originalMax, newMin, newMax) {
        let mappedValue = newMin + ((value - originalMin) * (newMax - newMin)) / (originalMax - originalMin);
        return mappedValue;
    }

    function animate() {
        if (mouseMovements.current && mouseIsDown.current && userNeedsToSlow.current && !userBubblePopped.current) {
            let movementToFilter = mouseMovements.current.shift();

            if (!movementToFilter) {
                movementToFilter = latestMousePositionRef.current;
            }

            let filteredX = filters.current.fx.filter(movementToFilter.x)
            let filteredY = filters.current.fy.filter(movementToFilter.y)

            latestFilteredMousePosition.current = { x: filteredX, y: filteredY };
            setMouseWidgetPosition({ x: filteredX - (mouseWidgetBounds.current.width / 2), y: filteredY - (mouseWidgetBounds.current.height / 2) });
        }

        else if (latestMousePositionRef.current && !userBubblePopped.current) {
            setMouseWidgetPosition({ x: latestMousePositionRef.current.x - (mouseWidgetBounds.current.width / 2), y: latestMousePositionRef.current.y - (mouseWidgetBounds.current.height / 2) });
        }
        requestRef.current = requestAnimationFrame(animate);
    }

    useEffect(() => {
        requestRef.current = requestAnimationFrame(animate);

        return () => {
            cancelAnimationFrame(requestRef.current);
        }
    }, []);

    // helper function to calculate the distance between two points
    function distance(x1, y1, x2, y2) {
        return Math.sqrt(((x2 - x1) * (x2 - x1)) + ((y2 - y1) * (y2 - y1)));
    }

    function popBubbleAnimation() {
        let currTime = Date.now();
        let mappedWidthMultiplier = mapRange(currTime, poppingAnimationStartTime.current, poppingAnimationEndTime.current, 1, 3);
        mappedWidthMultiplier = Math.min(3, mappedWidthMultiplier);
        mappedWidthMultiplier = Math.max(1, mappedWidthMultiplier);
        mouseWidgetWidthMultiplierRef.current = mappedWidthMultiplier;

        setMouseWidgetWidthMultiplier(mouseWidgetWidthMultiplierRef.current);
        let newWidth = mouseWidgetWidth * mouseWidgetWidthMultiplierRef.current;
        setMouseWidgetPosition({ x: poppingMouseWidgetPosition.current.x - (newWidth / 2), y: poppingMouseWidgetPosition.current.y - (newWidth / 2) });


        if (mouseWidgetWidthMultiplierRef.current >= 3) {
            cancelAnimationFrame(poppingRequestRef.current);
            poppingRequestRef.current = null;
            mouseWidgetRef.current.style.transition = "none";
        }

        else {
            poppingRequestRef.current = requestAnimationFrame(popBubbleAnimation);
        }

    }

    useEffect(() => {
        if (showMouseWidget) {

            mouseWidgetRef.current.style.transition = "none";
            mouseWidgetRef.current.style.opacity = 0.1;

            // clear the mouse movements
            mouseMovements.current = [{ x: latestMousePosition.x, y: latestMousePosition.y, added: "useEffect" }];

            filters.current = { fx: new OneEuroFilter(frequency, fcMin, beta, dcutoff), fy: new OneEuroFilter(frequency, fcMin, beta, dcutoff) };
            // filters.current = { fx: new LowPassFilter(0.5), fy: new LowPassFilter(0.5) };
            setMouseWidgetPosition({ x: latestMousePosition.x - (mouseWidgetBounds.current.width / 2), y: latestMousePosition.y - (mouseWidgetBounds.current.height / 2) });
            customizeTooltip();
            userBubblePopped.current = false;
        }
        else {
            if (userBubblePopped.current) {
                poppingMouseWidgetPosition.current = latestFilteredMousePosition.current;
                poppingAnimationStartTime.current = Date.now();
                poppingAnimationEndTime.current = poppingAnimationStartTime.current + 500; // 1 second animation
                poppingRequestRef.current = requestAnimationFrame(popBubbleAnimation);

                mouseWidgetRef.current.style.transition = "opacity 0.5s ease-in";
            }

            mouseWidgetRef.current.style.opacity = 0;
        }
    }, [showMouseWidget]);

    function mouseMove(e) {
        let textSelection = document.getSelection().toString();
        setLatestMousePosition({ x: e.clientX, y: e.clientY });
        latestMousePositionRef.current = { x: e.clientX, y: e.clientY, timestamp: Date.now() };

        mouseMovements.current.push({ x: e.clientX, y: e.clientY, added: "mouseMove" });

        // add the current mouse position to the list of mouse positions so we can filter them later
        if (mouseIsDown.current && textSelection.length > 0 && textSelection !== "") {
            // check if the mouse is within the circle
            let distanceFromCenter = distance(e.clientX, e.clientY, mouseWidgetPosition.x + (mouseWidgetBounds.current.width / 2), mouseWidgetPosition.y + (mouseWidgetBounds.current.height / 2));
            if ((distanceFromCenter > mouseWidgetBounds.current.width / 2) && userNeedsToSlow.current && showMouseWidget) {
                userBubblePopped.current = true;
                setShowPoppedTooltip(true);
                setShowMouseWidget(false);
                mouseWidgetRef.current.style.backgroundColor = "red";

                // log the highlight attempt
                let sel = document.getSelection();
                let unsnappedStartIndex = parseInt(sel.anchorNode.parentNode.getAttribute('data-position'));
                let unsnappedEndIndex = parseInt(sel.focusNode.parentNode.getAttribute('data-position'));
                let unsnappedText = properties.passage.substring(unsnappedStartIndex, unsnappedEndIndex + 1);
                let unsnappedRange = { start: unsnappedStartIndex, end: unsnappedEndIndex, text: unsnappedText };
                let startNodeBounds = sel.anchorNode.parentNode.getBoundingClientRect().toJSON();
                let endNodeBounds = sel.focusNode.parentNode.getBoundingClientRect().toJSON();
                let mousePosition = { mouseDown: mouseDownPosition.current, mousePopped: { x: e.clientX, y: e.clientY, timestamp: Date.now() } };
                let log = {
                    type: "pop",
                    timestamp: Date.now(),
                    workerId: properties.workerId,
                    participantId: properties.participantId,
                    condition: properties.condition,
                    experiment: properties.experiment,
                    pass: properties.pass,
                    tweakedParameter: mouseWidgetWidth,
                    selectedLevel: properties.selectedLevel,
                    unsnappedRange: unsnappedRange,
                    startNodeBounds: startNodeBounds,
                    endNodeBounds: endNodeBounds,
                    mousePosition: mousePosition
                }
                logHighlightEvent(log);
            }
        }
    }

    function customizeTooltip() {
        if (tooltipRef.current && mouseWidgetRef.current) {
            let tooltip = tooltipRef.current.UNSAFE_getDOMNode();
            let tooltipBounds = tooltip.getBoundingClientRect();

            setTooltipOffset({ x: (tooltipBounds.width / 2) - (mouseWidgetBounds.current.width / 2), y: /*(tooltipBounds.height/2) - (mouseWidgetBounds.current.height / 2)*/ 40 });
        }

        if (poppedTooltipRef.current) {
            let poppedTooltip = poppedTooltipRef.current.UNSAFE_getDOMNode();
            let poppedTooltipBounds = poppedTooltip.getBoundingClientRect();
            setPoppedTooltipOffset({ x: (poppedTooltipBounds.width / 2), y: (poppedTooltipBounds.height / 2) + 25 });

        }
    }

    useEffect(() => {
        let mWidgetBounds = mouseWidgetRef.current.getBoundingClientRect();
        if (mWidgetBounds.width > 0 && mWidgetBounds.height > 0) {
            mouseWidgetBounds.current = mWidgetBounds;
        }
        customizeTooltip();
    }, [mouseWidgetPosition, mouseWidgetWidth, mouseWidgetWidthMultiplier]);

    return (
        <>

            <Tooltip UNSAFE_className="no-select" ref={poppedTooltipRef} isOpen={showPoppedTooltip} UNSAFE_style={{ textAlign: "center", display: "unset" }} left={latestMousePosition.x - poppedTooltipOffset.x} top={latestMousePosition.y - poppedTooltipOffset.y} placement='top' position={"fixed"} zIndex={10000}>Your cursor left the circle! Try again.</Tooltip>
            <Tooltip UNSAFE_className="no-select" ref={tooltipRef} isOpen={showMouseWidget && !showPoppedTooltip} UNSAFE_style={{ textAlign: "center", display: "unset" }} left={mouseWidgetPosition.x - tooltipOffset.x} top={mouseWidgetPosition.y - tooltipOffset.y} placement='top' position={"fixed"} zIndex={10000}>{mouseWidgetInstructions}</Tooltip>
            <div ref={mouseWidgetRef} className='circle no-select' style={{ left: mouseWidgetPosition.x, top: mouseWidgetPosition.y, width: (mouseWidgetWidth * mouseWidgetWidthMultiplier), height: (mouseWidgetWidth * mouseWidgetWidthMultiplier) }} /*hidden={(!showMouseWidget || showPoppedTooltip) && poppingAnimationOver}*/></div>
            <div id="textToRead" className='tweakingDocumentReader' onMouseDown={e => mouseDown(e)} onCopy={e => e.preventDefault()} onMouseUp={e => mouseUp(e)} onMouseMove={e => mouseMove(e)}>

                <Highlightable
                    ranges={properties.highlights}
                    enabled={properties.maxWordCount === 0 ? false : true} // disable highlighting if the max word count is 0
                    highlightStyle={range => {
                        if (range.highlight) {
                            return {
                                backgroundColor: '#FDEAA3',
                            }
                        }
                        else {
                            return {
                                backgroundColor: '#b3d8ff',
                            }
                        }
                    }}
                    onTextHighlighted={onTextHighlighted}
                    text={properties.passage}
                    nodeRenderer={(index, range, text) => {
                        var currChar = text.charAt(index);
                        if (currChar === '\n') {
                            return (<><br /><br /></>)
                        }
                        return (
                            <span data-position={index} range={range}>{currChar}</span>
                        )
                    }}
                    rangeRenderer={range => {
                        // helper function to update the tool bar visibility
                        function updateShowToolbar(newValue) {
                            var index = properties.highlights.indexOf(range[0].props.range);
                            var newHighlight = properties.highlights[index];
                            newHighlight.showToolbar = newValue;
                            properties.setHighlights(properties.highlights.toSpliced(index, 1).concat(newHighlight));
                        }

                        function mouseEnter(e) {
                            e.style.outline = "2px solid #337AC2";
                            e.style.cursor = "pointer";
                        }

                        function mouseLeave(e) {
                            if (range[0].props.range.highlight)
                                e.style.outline = "2px solid #FDEAA3";
                            else
                                e.style.outline = "2px solid #b3d8ff";
                            e.style.cursor = "auto";
                        }

                        function mouseClick(e) {
                            updateShowToolbar(true);
                        }

                        // creating an actual highlight
                        if (range[0].props.range.highlight) {
                            return (
                                <div style={{ display: 'inline', position: 'relative' }}>
                                    <SelectionToolbar
                                        passage={properties.passage}
                                        countNumWords={properties.countNumWords}

                                        wordCount={properties.wordCount}
                                        setWordCount={properties.setWordCount}
                                        setProgressWordCount={properties.setProgressWordCount}

                                        selectedTool={'highlighter'}
                                        updateShowToolbar={updateShowToolbar}
                                        workerId={properties.workerId}
                                        participantId={properties.participantId}
                                        condition={properties.condition}
                                        experiment={properties.experiment}
                                        pass={properties.pass}
                                        tweakedParameter={mouseWidgetWidth}

                                        range={range}
                                        highlights={properties.highlights}
                                        setHighlights={properties.setHighlights} />
                                    <span style={{ outline: "2px solid #FDEAA3", backgroundColor: "#FDEAA3" }} onMouseEnter={e => mouseEnter(e.target.parentNode)} onMouseLeave={e => mouseLeave(e.target.parentNode)} onClick={e => mouseClick(e.target.parentNode)}>{range}</span>
                                </div>
                            );

                        }

                        // creating a fake selection effect
                        else {
                            return (
                                <div style={{ display: 'inline', position: 'relative' }}>
                                    <SelectionToolbar
                                        passage={properties.passage}
                                        countNumWords={properties.countNumWords}

                                        wordCount={properties.wordCount}
                                        setWordCount={properties.setWordCount}
                                        setProgressWordCount={properties.setProgressWordCount}
                                        setShadowWordCount={properties.setShadowWordCount}

                                        selectedTool={'cursor'}
                                        updateShowToolbar={updateShowToolbar}

                                        range={range}

                                        highlights={properties.highlights}
                                        setHighlights={properties.setHighlights} />
                                    <span style={{ outline: "2px solid #b3d8ff", backgroundColor: "#b3d8ff" }}>{range}</span>
                                </div>)
                                ;
                        }
                    }}
                />


                {/* {properties.passage} */}

            </div>
        </>
    );
}

export default TweakingTrailingWidgetDocument;